/**
 * Merged ordered IDs + per-country counts + creative labels for multi-country bulk edit modal.
 */

import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { mergeBulkEditDraftIds } from './bulkEditAffectedIds';
import { buildBulkEditDropLabelMapsFromOrdered } from './bulkEditDropLabels';
import { buildGlobalCountrySequence } from './perDropGeneration';

export interface BulkEditModalCatalogByCountry {
  leadsLists?: Array<{ id: number; name: string }>;
}

export interface BulkEditModalMergeState {
  mergedOrderedIds: string[];
  dropCountByCountry: Map<string, number>;
  bulkEditTargets: {
    orderedIds: string[];
    globalCountryPerDrop: string[];
    flat: DropLabel[];
    byCountry: Record<string, DropLabel[]>;
  };
}

function dropCountByCountryFromIds(ids: string[], mergedDrops: PreparedDrop[]): Map<string, number> {
  const byId = new Map(mergedDrops.map((d) => [d.id, d]));
  const map = new Map<string, number>();
  for (const id of ids) {
    const d = byId.get(id);
    if (!d) continue;
    map.set(d.countryKey, (map.get(d.countryKey) ?? 0) + 1);
  }
  return map;
}

export function buildBulkEditModalMergeState(
  selectedIds: string[],
  propagateToCountries: string[],
  mergedDrops: PreparedDrop[],
  catalogByCountry: Record<string, BulkEditModalCatalogByCountry>,
): BulkEditModalMergeState {
  const mergedOrderedIds = mergeBulkEditDraftIds(selectedIds, propagateToCountries, mergedDrops);
  const dropCountByCountry = dropCountByCountryFromIds(mergedOrderedIds, mergedDrops);
  const leadsListsByCountry = Object.fromEntries(
    Object.entries(catalogByCountry).map(([ck, c]) => [ck, c.leadsLists ?? []]),
  );
  const orderedIds = mergedOrderedIds;
  const labels = buildBulkEditDropLabelMapsFromOrdered(orderedIds, mergedDrops, leadsListsByCountry);
  return {
    mergedOrderedIds,
    dropCountByCountry,
    bulkEditTargets: {
      orderedIds,
      globalCountryPerDrop: buildGlobalCountrySequence(orderedIds, mergedDrops),
      flat: labels.flat,
      byCountry: labels.byCountry,
    },
  };
}
